import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Col, Row, Typography } from 'antd';
import { Card, Currency, HistoryDropDown, Icon } from '@common/components';
import { TermSummary } from 'works/components/term-summary';
import { EditAssignmentModal } from './edit-assignment-modal';
import { pluralizeWeeksValue } from 'works/legacy/services/length-in-weeks-service';

const { Title, Text } = Typography;

export function AssignmentExtensionSummary({
  staffingRequestAssignmentId,
  assignmentState,
  terms,
  isExtension,
  extensionReason,
  estimatedBudget,
  percentageComplete,
  startDate,
  endDate,
  children,
  sourceHistory,
  lengthInWeeks,
  ...other
}) {
  const isCancelled = assignmentState === 'cancelled';
  const isUpcoming = !isCancelled && percentageComplete === 0;
  const [showEditTermModal, setShowEditTermModal] = useState(false);

  const sortedTerms = [...terms].sort(
    (aa, bb) =>
      new Date(bb.effectiveAt) - new Date(aa.effectiveAt) ||
      new Date(bb.createdAt) - new Date(aa.createdAt)
  );

  const activeTerms = sortedTerms.filter((term) => !term.irrelevant);
  const inactiveTerms = sortedTerms.filter((term) => term.irrelevant);

  const allowEditAssignment = terms[0].canUpdate.value;

  const allowEditTerm = (termId) =>
    allowEditAssignment &&
    activeTerms
      .filter((term) => term.currentlyActive || term.upcoming)
      .map((term) => term.id)
      .includes(termId);

  const activeTermsEffectiveDates = activeTerms.map(
    ({ effectiveAt }) => effectiveAt
  );

  return (
    <Card className="assignment-extension-summary cursor-auto" {...other}>
      <Row style={{ alignItems: 'baseline' }}>
        <Col>
          <Title level={4}>
            {isUpcoming && isExtension && <span>Upcoming</span>}
            {isCancelled && <span>Cancelled</span>}{' '}
            {isExtension ? 'Extension:' : 'Assignment:'}{' '}
            {moment(startDate).format('MM/DD/YY')}
            {' - '}
            {moment(endDate).format('L')}
          </Title>
        </Col>

        {allowEditAssignment && (
          <>
            <Col flex={2}>
              <EditAssignmentModal
                activeTermsEffectiveDates={activeTermsEffectiveDates}
                showModal={showEditTermModal}
                onClose={() => setShowEditTermModal(false)}
                assignmentTermId={activeTerms[0].id}
                isExtension={isExtension}
              />
              <Button type="link" onClick={() => setShowEditTermModal(true)}>
                <Icon icon="pencil" color="elm" size="lg" />
              </Button>
            </Col>
          </>
        )}
      </Row>

      <Row gutter={[0, 4]}>
        <Col span={3}>
          <Text type="secondary">Length: </Text>
        </Col>
        <Col>
          <Text>{pluralizeWeeksValue(lengthInWeeks)}</Text>
        </Col>
      </Row>

      <Row gutter={[0, 4]}>
        <Col span={3}>
          <Text type="secondary">Estimated Budget: </Text>
        </Col>
        <Col>
          <Text>
            <Currency value={estimatedBudget} />
          </Text>
        </Col>
      </Row>

      {sourceHistory.length > 0 && (
        <HistoryDropDown
          data={sourceHistory}
          button={false}
          hrVisible={false}
          dropdownText="View Changes"
        />
      )}

      {activeTerms.map((term) => {
        return (
          <section
            key={term.id}
            className={
              sourceHistory.length > 0
                ? 'SummarySectionMarginBottom'
                : 'SummarySectionMargin'
            }
          >
            <TermSummary
              allowEditTerm={allowEditTerm(term.id)}
              key={`summary-${term.id}`}
              term={term}
              historyTerms={inactiveTerms.filter(
                (t) => t.effectiveAt === term.effectiveAt
              )}
              isExtension={isExtension}
              isActiveTerm={isCancelled ? false : term.currentlyActive}
              extensionReason={extensionReason}
            />
          </section>
        );
      })}

      {children}

      <style jsx>{`
        .summary-duration {
          font-weight: 400;
          font-size: 20px;
        }
        .edit-area {
          display: flex;
          justify-content: flex-end;
        }
        :global(.assignment-extension-summary .SummarySectionMargin) {
          margin: 24px 0;
        }
        :global(.assignment-extension-summary .SummarySectionMarginBottom) {
          margin: 0 0 24px 0;
        }
        :global(.assignment-extension-summary .history-dropdown) {
          margin-top: 16px;
          margin-bottom: 16px;
        }
      `}</style>
    </Card>
  );
}

AssignmentExtensionSummary.propTypes = {
  staffingRequestAssignmentId: PropTypes.string.isRequired,
  assignmentState: PropTypes.string.isRequired,
  isExtension: PropTypes.bool,
  sourceHistory: PropTypes.array,
  extensionReason: PropTypes.string,
  percentageComplete: PropTypes.number.isRequired,
  estimatedBudget: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  lengthInWeeks: PropTypes.number.isRequired,
  terms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      currentlyActive: PropTypes.bool.isRequired,
      upcoming: PropTypes.bool.isRequired,
      effectiveAt: PropTypes.string.isRequired,
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      billingRegularRate: PropTypes.number.isRequired,
      shiftsPerWeek: PropTypes.number.isRequired,
      hoursPerShift: PropTypes.number.isRequired,
      lengthInWeeks: PropTypes.number.isRequired,
      shiftType: PropTypes.string.isRequired,
      timeoffDescription: PropTypes.string,
      createdByName: PropTypes.string,
      canUpdate: PropTypes.shape({ value: PropTypes.bool.isRequired }),
    })
  ),
  children: PropTypes.node,
};

AssignmentExtensionSummary.defaultProps = {
  isExtension: false,
};
